import { useEffect, useRef, useState } from 'react'
import { MessageSquare, Send } from 'lucide-react'
import { Link, usePath } from '../router.js'
import { useToast } from '../admin/primitives.js'
import { chargerActivite, commenter } from './signalements-api.js'
import {
  Intentions, cheminFiche, dateHeureParis, decrireEvenement, limite, nomDe,
  type ErreurSig, type Evenement, type MetaSignalements,
} from './signalements-model.js'
import { Chargement, EtatErreur, ZoneTexte } from './SignalementsCommun.js'

/**
 * SIGNALEMENTS — L'ACTIVITÉ D'UNE FICHE (09/10/2026).
 *
 * Technique est la source de vérité, et son historique EST l'histoire du
 * signalement : création, modifications, statuts, responsables, liens,
 * pièces jointes, commentaires. On l'affiche dans l'ordre CHRONOLOGIQUE où
 * Technique le rend, page après page (`next_cursor`, opaque), avec les
 * libellés de `meta.evenements` et des détails écrits en phrases — jamais un
 * objet brut. Les commentaires y vivent aussi : un ajout seul, sans édition
 * ni suppression (Technique n'en connaît pas).
 */

type Etat =
  | { phase: 'chargement' }
  | { phase: 'ok'; evenements: Evenement[]; curseur: string | null }
  | { phase: 'erreur'; erreur: ErreurSig }

const INTENTION = 'commentaire'

export function Activite(props: {
  reference: string
  meta: MetaSignalements
  /** Change à chaque mutation de la fiche : l'historique se relit. */
  relecture: number
  peutCommenter: boolean
  onCommentaire: () => void
}) {
  const { reference, meta } = props
  const toast = useToast()
  const [etat, setEtat] = useState<Etat>({ phase: 'chargement' })
  const [suite, setSuite] = useState(false)
  const [suiteErreur, setSuiteErreur] = useState<ErreurSig | null>(null)
  const [texte, setTexte] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreurEnvoi, setErreurEnvoi] = useState<ErreurSig | null>(null)
  const [essai, setEssai] = useState(0)
  const intentions = useRef(new Intentions())
  useEffect(() => () => intentions.current.toutOublier(), [])
  // Une page large : la plupart des fiches tiennent en un appel (le débit vers Technique est partagé).
  const taille = limite(meta, 'activite_max')

  useEffect(() => {
    let vivant = true
    void chargerActivite(reference, taille, null).then((r) => {
      if (!vivant) return
      setSuiteErreur(null)
      setEtat(r.ok ? { phase: 'ok', evenements: r.data.items, curseur: r.data.next_cursor } : { phase: 'erreur', erreur: r.erreur })
    })
    return () => { vivant = false }
  }, [reference, taille, props.relecture, essai])

  const chargerSuite = async () => {
    if (etat.phase !== 'ok' || !etat.curseur) return
    setSuite(true)
    const r = await chargerActivite(reference, taille, etat.curseur)
    setSuite(false)
    if (r.ok) {
      const vus = new Set(etat.evenements.map((e) => e.id))
      setEtat({ phase: 'ok', evenements: [...etat.evenements, ...r.data.items.filter((e) => !vus.has(e.id))], curseur: r.data.next_cursor })
      setSuiteErreur(null)
    } else if (r.erreur.genre === 'curseur') {
      // Le curseur ne vaut plus : on relit l'historique depuis le début.
      setEssai((n) => n + 1)
    } else setSuiteErreur(r.erreur)
  }

  const publier = async () => {
    const t = texte.trim()
    if (!t || envoi) return
    setEnvoi(true)
    setErreurEnvoi(null)
    // Même intention tant que le commentaire n'est pas publié : un nouvel essai ne le double pas.
    const r = await commenter(reference, texte, intentions.current.cle(INTENTION))
    intentions.current.apres(INTENTION, r.ok ? null : r.erreur)
    setEnvoi(false)
    if (!r.ok) { setErreurEnvoi(r.erreur); return }
    setTexte('')
    toast('ok', 'Commentaire ajouté')
    props.onCommentaire()
  }

  const max = limite(meta, 'commentaire')
  return (
    <section className="lc-panel sig-section" aria-labelledby="sig-activite">
      <div className="sig-section__corps">
        <h2 className="sig-section__titre" id="sig-activite">Activité</h2>
        {etat.phase === 'chargement' ? <Chargement libelle="Chargement de l’activité" /> : null}
        {etat.phase === 'erreur' ? <EtatErreur erreur={etat.erreur} quoi="l’activité" compact onReessayer={() => setEssai((n) => n + 1)} /> : null}
        {etat.phase === 'ok' ? (
          etat.evenements.length === 0 ? <p className="sig-aide">Aucun événement pour l’instant.</p> : (
            <ol className="sig-fil">
              {etat.evenements.map((ev) => <LigneFil key={ev.id} ev={ev} meta={meta} />)}
            </ol>
          )
        ) : null}
        {etat.phase === 'ok' && etat.curseur ? (
          <button type="button" className="lc-btn lc-btn--sm" onClick={() => void chargerSuite()} disabled={suite}>
            {suite ? 'Chargement…' : 'Afficher la suite de l’historique'}
          </button>
        ) : null}
        {suiteErreur ? <EtatErreur erreur={suiteErreur} compact /> : null}

        {props.peutCommenter ? (
          <div className="sig-commenter">
            <label className="lc-label" htmlFor="sig-commentaire">Ajouter un commentaire</label>
            <ZoneTexte id="sig-commentaire" value={texte} onChange={setTexte} rows={3} maxLength={max}
              placeholder="Ce que vous avez constaté, fait ou prévu." disabled={envoi} />
            <div className="sig-commenter__pied">
              <span className="sig-aide">{texte.length > max * 0.8 ? `${texte.length} / ${max}` : 'Visible de toute l’équipe. Un commentaire ne se modifie pas.'}</span>
              <button type="button" className="lc-btn lc-btn--primary" disabled={!texte.trim() || envoi} onClick={() => void publier()}>
                <Send size={15} aria-hidden="true" /> {envoi ? 'Envoi…' : erreurEnvoi ? 'Réessayer' : 'Publier'}
              </button>
            </div>
            {erreurEnvoi ? <EtatErreur erreur={erreurEnvoi} compact /> : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}

function LigneFil({ ev, meta }: { ev: Evenement; meta: MetaSignalements }) {
  const l = decrireEvenement(ev, meta)
  const chemin = usePath()
  const estCommentaire = ev.type === 'comment_added'
  return (
    <li className={`sig-fil__item${estCommentaire ? ' sig-fil__item--commentaire' : ''}`}>
      <span className="sig-fil__point" aria-hidden="true">{estCommentaire ? <MessageSquare size={12} /> : null}</span>
      <div className="sig-fil__corps">
        <p className="sig-fil__tete">
          <strong>{estCommentaire ? nomDe(ev.actor, 'Technique') : l.titre}</strong>
          <span className="sig-aide">
            {estCommentaire ? 'a commenté' : `par ${nomDe(ev.actor, 'Technique')}`} · <time dateTime={ev.created_at}>{dateHeureParis(ev.created_at)}</time>
          </span>
        </p>
        {l.details.length > 0 ? (
          <ul className="sig-fil__details">
            {l.details.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        ) : null}
        {l.reference && cheminFiche(l.reference) !== chemin ? (
          <Link href={cheminFiche(l.reference)} className="sig-lien">Ouvrir {l.reference}</Link>
        ) : null}
        {l.texteLibre ? <p className={estCommentaire ? 'sig-fil__commentaire' : 'sig-fil__texte'}>{l.texteLibre}</p> : null}
      </div>
    </li>
  )
}
